import { useEffect, useMemo, useState } from 'react';
import { Tree, Typography, Space, Tabs } from 'antd';
import {
  AppstoreOutlined,
  SettingOutlined,
  FormatPainterOutlined,
  AimOutlined,
  GlobalOutlined,
  BulbOutlined,
  FieldTimeOutlined,
  ApiOutlined,
  ClusterOutlined,
  DeploymentUnitOutlined,
  ToolOutlined,
  ExperimentOutlined,
  VideoCameraOutlined,
  CameraOutlined,
  CarOutlined,
  ScissorOutlined,
  ThunderboltOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import type { DataNode } from 'antd/es/tree';
import SceneTreeView from './SceneTreeView';
import SceneSettingsPanel from './SceneSettingsPanel';
import Chart3DPanel from './Chart3DPanel';
import BirdsEyeCameraPanel from './BirdsEyeCameraPanel';
import ModelPanel from './ModelPanel';
import ModelLibrary from './ModelLibrary';
import LayoutToolbar from './LayoutToolbar';
import LayerPanel from './LayerPanel';
// 2026-08-11 移除：左侧"材质"面板(MaterialEditor.tsx)对嵌套模型无效且不持久化，材质统一在右侧属性面板「材质」页签操作
import MeasurementTool from './MeasurementTool';
import EnvironmentPanel from './EnvironmentPanel';
import EffectsPanel from './EffectsPanel';
import LightPanel from './LightPanel';
import TimelinePanel from './TimelinePanel';
import GisControlPanel from '../gis/GisControlPanel';
import GisLayerPanel from '../gis/GisLayerPanel';
import GeoFencePanel from '../gis/GeoFencePanel';
import HeatmapPanel from '../gis/HeatmapPanel';
import CameraPathPanel from './CameraPathPanel';
import CarouselPanel from './CarouselPanel';
import ClippingPanel from './ClippingPanel';
import FlyLinePanel from './FlyLinePanel';
import IotPanel from '../iot/IotPanel';
import DataSourcePanel from '../data/DataSourcePanel';
import BindingPanel from '../data/BindingConfigPanel';
import { KeyframeEngine } from '../../core/KeyframeEngine';
import useSceneStore from '../../stores/sceneStore';
import useGisStore from '../../stores/gisStore';
import useEditorUiStore from '../../stores/editorUiStore';
import { useSelectionStore } from '../../stores/selectionStore';

const { Text } = Typography;

interface PropertyTreePanelProps {
  selectedModelId: string | null;
  keyframeEngine: KeyframeEngine;
}

export default function PropertyTreePanel({ selectedModelId, keyframeEngine }: PropertyTreePanelProps) {
  const [expandedKeys, setExpandedKeys] = useState<string[]>(['3d-editor']);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  // 2026-10-15（P0-③ 场景树）：顶部 Tab「场景树」/「功能」；恢复的 activePanel 定位在「功能」段
  const [tab, setTab] = useState<'scene-tree' | 'function'>('scene-tree');
  const activePanel = useEditorUiStore((s) => s.activePanel);
  const setActivePanel = useEditorUiStore((s) => s.setActivePanel);
  const chart3dSelectedId = useSelectionStore((s) => s.chart3dSelectedId);
  useEffect(() => {
    if (chart3dSelectedId) setTab('function');
  }, [chart3dSelectedId]);
  const { gisEnabled } = useGisStore();
  const { setLayers } = useGisStore();
  const { gisState } = useGisStore();
  const { models } = useSceneStore();

  const handleAnimationApply = (targetId: string, property: string, value: number[]) => {
    // Animation apply logic
  };

  const treeData = useMemo<DataNode[]>(() => [
    {
      title: '3D编辑器',
      key: '3d-editor',
      icon: <AppstoreOutlined />,
      children: [
        {
          title: '场景',
          key: 'scene',
          icon: <AppstoreOutlined />,
        },
        {
          title: '模型',
          key: 'models',
          icon: <SettingOutlined />,
        },
        {
          title: '模型库',
          key: 'model-library',
          icon: <ClusterOutlined />,
        },
        {
          title: '工具',
          key: 'tools',
          icon: <ToolOutlined />,
        },
        {
          title: '图层',
          key: 'layers',
          icon: <DeploymentUnitOutlined />,
        },
        {
          title: '测量',
          key: 'measurement',
          icon: <AimOutlined />,
        },
        {
          title: '环境',
          key: 'environment',
          icon: <GlobalOutlined />,
        },
        {
          title: '特效',
          key: 'effects',
          icon: <ExperimentOutlined />,
        },
        {
          title: '灯光',
          key: 'lights',
          icon: <BulbOutlined />,
        },
        {
          title: '时间轴',
          key: 'timeline',
          icon: <FieldTimeOutlined />,
        },
        {
          title: 'GIS',
          key: 'gis',
          icon: <GlobalOutlined />,
        },
        {
          title: '漫游',
          key: 'camera',
          icon: <VideoCameraOutlined />,
        },
        {
          // 2026-10-15（P0-④）：鸟瞰相机（首个启用入口；启用后场景树出现 cam:birdseye 节点）
          title: '鸟瞰相机',
          key: 'birdsEye',
          icon: <CameraOutlined />,
        },
        {
          title: '轮播',
          key: 'carousel',
          icon: <CarOutlined />,
        },
        {
          title: '剖面',
          key: 'clipping',
          icon: <ScissorOutlined />,
        },
        {
          title: '飞线',
          key: 'flylines',
          icon: <ThunderboltOutlined />,
        },
        {
          title: '3D 图表',
          key: 'chart3d',
          icon: <BarChartOutlined />,
        },
        {
          title: 'IoT',
          key: 'iot',
          icon: <ApiOutlined />,
        },
        {
          title: '数据',
          key: 'data',
          icon: <ApiOutlined />,
        },
      ],
    },
  ], []);

  const handleSelect = (keys: React.Key[]) => {
    if (keys.length > 0) {
      const key = keys[0] as string;
      setSelectedKeys([key]);
      setActivePanel(key);
      // 2026-09-07｜GIS 模式联动：点「GIS」菜单自动进入 GIS 模式；点其他属性菜单自动退出 GIS 切回模型 3D 场景
      // 2026-09-14 修复（测试稳定性）：getState 可选调用——部分单测 mock gisStore 未提供 getState，
      // 直接调用会抛 TypeError（unhandled error 污染测试报告）
      const gisStoreAny = useGisStore as any;
      if (key === 'gis') {
        gisStoreAny.getState?.()?.setGisEnabled?.(true);
      } else {
        gisStoreAny.getState?.()?.setGisEnabled?.(false);
      }
    }
  };

  // 同步外部 activePanel (DataMenu/FusionMenu/场景树子系统点击) 到 selectedKeys
  const [prevActivePanel, setPrevActivePanel] = useState<string | null>(null);
  if (activePanel !== prevActivePanel) {
    setPrevActivePanel(activePanel);
    if (activePanel) {
      setSelectedKeys([activePanel]);
      setTab('function');
      if (!expandedKeys.includes('3d-editor')) {
        setExpandedKeys(['3d-editor']);
      }
    }
  }

  const renderPanel = () => {
    switch (activePanel) {
      // 2026-10-15（P0-③）：原「场景」模型树并入顶部「场景树」Tab；此入口改为场景属性面板（P0-② 入口①）
      case 'scene':
        return <SceneSettingsPanel />;
      // 2026-10-15（P0-④）：鸟瞰相机属性面板（场景树 cam:birdseye 节点进入）
      case 'birdsEye':
        return <BirdsEyeCameraPanel />;
      case 'models':
        return <ModelPanel />;
      case 'model-library':
        return <ModelLibrary />;
      case 'tools':
        return (
          <div style={{ padding: '8px 12px' }}>
            <LayoutToolbar />
          </div>
        );
      case 'layers':
        return <LayerPanel />;
      case 'measurement':
        return <MeasurementTool />;
      case 'environment':
        return <EnvironmentPanel />;
      case 'effects':
        return <EffectsPanel />;
      case 'lights':
        return <LightPanel />;
      case 'timeline':
        return (
          <TimelinePanel
            engine={keyframeEngine}
            selectedNodeId={selectedModelId}
            onApplyValues={handleAnimationApply}
          />
        );
      case 'gis':
        return (
          <>
            <GisControlPanel />
            <GisLayerPanel layers={gisState.layers} onLayersChange={setLayers} />
            <GeoFencePanel />
            <HeatmapPanel />
          </>
        );
      case 'camera':
        return <CameraPathPanel />;
      case 'carousel':
        return <CarouselPanel />;
      case 'clipping':
        return <ClippingPanel />;
      case 'flylines':
        return <FlyLinePanel />;
      case 'chart3d':
        return <Chart3DPanel />;
      case 'iot':
        return <IotPanel />;
      case 'data':
        return (
          <>
            <DataSourcePanel />
            <BindingPanel selectedNodeId={selectedModelId || null} />
          </>
        );
      default:
        return (
          <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
            请选择左侧功能菜单
          </div>
        );
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#1a1a2e' }}>
      <div style={{ padding: '0 8px', borderBottom: '1px solid #30475e' }}>
        <Tabs
          size="small"
          activeKey={tab}
          onChange={(k) => setTab(k as 'scene-tree' | 'function')}
          items={[
            { key: 'scene-tree', label: '场景树' },
            { key: 'function', label: '功能' },
          ]}
        />
      </div>
      {tab === 'scene-tree' ? (
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <SceneTreeView />
        </div>
      ) : (
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <div style={{ width: 120, borderRight: '1px solid #30475e', overflow: 'auto' }}>
            <Tree
              showIcon
              defaultExpandedKeys={['3d-editor']}
              selectedKeys={selectedKeys}
              onSelect={handleSelect}
              treeData={treeData}
              style={{ fontSize: 12 }}
              className="property-tree-nav"
            />
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            {renderPanel()}
          </div>
        </div>
      )}
    </div>
  );
}
